'use client';

import { type ReactNode } from 'react';
import { PAGE_ACTION_FLOOR } from './page-action-floor';

export interface PageActionsProps {
  /**
   * The page's one primary action: a `Button`, or a link styled as one. Below `sm` it becomes the
   * row's full-width 44px control.
   */
  primary?: ReactNode;
  /**
   * Up to two secondary controls shown beside the primary (ghost or secondary buttons, a link).
   * Pass the controls themselves, not a wrapper around them: the 44px floor applies to each direct
   * child of this slot.
   */
  secondary?: ReactNode;
  /**
   * Everything past the visible primary and two secondaries: the overflow menu, as a rendered
   * `<ActionMenu items={...} label="More actions" triggerClassName={PAGE_ACTION_MENU_TRIGGER} />`.
   * It is a node, not a list of items, on purpose: this component must not import `ActionMenu`,
   * because `ListPage`, `DashboardPage` and `ReportFrame` import this one, and an `ActionMenu` edit
   * would then select the specs of every page built on them (`selection-graph.test.ts` pins that a
   * single `ActionMenu` change stays under 250 specs). Omit it when there is nothing to put in it.
   */
  menu?: ReactNode;
  className?: string;
}

/**
 * The action grammar of a page header (page header contract H7, #16194): one primary, at most two
 * secondary controls visible, the rest in an overflow menu.
 *
 * - From `sm` up the controls sit in one row at the header's trailing edge (secondary, primary,
 *   menu), as the archetypes always composed them.
 * - Below `sm` the row takes the full width under the title. The primary fills what the secondaries
 *   leave on their line and wraps onto a full-width line of its own when it does not fit (it is
 *   never shrunk below its label); the secondaries and the menu trigger keep their width. The
 *   header no longer wraps its actions to a ragged second line of 28 to 40px controls.
 * - Below `md` every control the caller passed is at least 44px tall (`PAGE_ACTION_FLOOR`), and the
 *   menu trigger is 44px square; from `md` up nothing changes size.
 *
 * `PageHeader` renders this component as the title row's own trailing item (it does not wrap it a
 * second time), which is what lets the row take the full width below `sm`. Passed anywhere else it
 * is still a flex row; it just does not get the full-width behaviour.
 *
 * `PageHeader.actions` stays a `ReactNode`: any other content keeps working unchanged, with no
 * floor, because the header cannot tell a primary from a secondary or move arbitrary JSX into a
 * menu.
 */
export function PageActions({ primary, secondary, menu, className }: PageActionsProps) {
  if (!primary && !secondary && !menu) return null;
  return (
    <div
      data-page-actions=""
      className={['flex max-w-full flex-wrap items-center gap-2 max-sm:w-full', className]
        .filter(Boolean)
        .join(' ')}
    >
      {/* `empty:hidden`: a secondary that renders nothing (a Fragment of conditionals that are all
          false) must not leave an empty slot in the row, where its gap would indent the primary. */}
      {secondary ? (
        <div
          className={[
            'flex max-w-full flex-wrap items-center gap-2 empty:hidden',
            PAGE_ACTION_FLOOR,
          ].join(' ')}
        >
          {secondary}
        </div>
      ) : null}
      {/* Below `sm` the primary fills what the secondaries leave on their line (`flex-auto`: grow from
          its own width) and wraps onto a full-width line of its own when it does not fit. It must never
          be allowed to shrink below its content (`min-w-0` or a zero basis crushed it to 56px with its
          label outside the button, H7 judge round): no `min-w-0` here, on purpose. */}
      {primary ? (
        <div
          className={[
            'flex items-center empty:hidden max-sm:flex-auto max-sm:*:w-full',
            PAGE_ACTION_FLOOR,
          ].join(' ')}
        >
          {primary}
        </div>
      ) : null}
      {menu ?? null}
    </div>
  );
}

// The marker `PageHeader` reads to recognise this component without importing it, which would put
// `ActionMenu` (and its portal and positioning code) into the dependency graph of every `PageHeader`
// consumer. A wrapper around the element (memo, a Fragment) hides the marker, and the failure is
// benign: the header falls back to its plain wrapper, so the row just does not get the full-width
// behaviour below `sm`.
PageActions.isPageActions = true;
